import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

@Component({
  selector: 'app-pitch-deck',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pitch-deck.html',
  styleUrls: ['./pitch-deck.scss'],
})
export class PitchDeck implements OnInit, OnDestroy {
  @Output() pitchComplete = new EventEmitter<void>();

  private readonly router = inject(Router);

  currentSection = 0;
  totalSections = 4;
  isScrolling = false;

  private autoScrollInterval: any;
  private readonly AUTO_SCROLL_DELAY = 20000; // 20 seconds

  // Touch/swipe handling
  private touchStartY = 0;
  private touchEndY = 0;
  private readonly SWIPE_THRESHOLD = 50; // Minimum distance for a swipe

  ngOnInit() {
    // Initialize the pitch deck and start auto-scroll
    this.startAutoScroll();
  }

  ngOnDestroy() {
    // Clean up the auto-scroll timer
    this.stopAutoScroll();
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    if (this.isScrolling) return;

    // Pause auto-scroll when user interacts
    this.pauseAutoScroll();

    this.isScrolling = true;

    if (event.deltaY > 0 && this.currentSection < this.totalSections - 1) {
      // Scroll down
      this.currentSection++;
    } else if (event.deltaY < 0 && this.currentSection > 0) {
      // Scroll up
      this.currentSection--;
    }

    // Reset scrolling flag after animation
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    // Don't advance if clicking on navigation dots or continue button
    const target = event.target as HTMLElement;
    if (target.closest('.navigation-dots') || target.closest('.continue-btn')) {
      return;
    }

    if (this.isScrolling) return;

    // Pause auto-scroll when user interacts
    this.pauseAutoScroll();

    this.isScrolling = true;

    if (this.currentSection < this.totalSections - 1) {
      this.currentSection++;
    }

    // Reset scrolling flag after animation
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    // Don't handle swipe if touching navigation dots or continue button
    const target = event.target as HTMLElement;
    if (target.closest('.navigation-dots') || target.closest('.continue-btn')) {
      return;
    }

    if (this.isScrolling) return;

    // Store the starting Y position
    this.touchStartY = event.touches[0].clientY;
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    // Don't handle swipe if touching navigation dots or continue button
    const target = event.target as HTMLElement;
    if (target.closest('.navigation-dots') || target.closest('.continue-btn')) {
      return;
    }

    if (this.isScrolling) return;

    // Store the ending Y position
    this.touchEndY = event.changedTouches[0].clientY;

    // Calculate the swipe distance
    const swipeDistance = this.touchStartY - this.touchEndY;

    // Check if it's a valid swipe
    if (Math.abs(swipeDistance) > this.SWIPE_THRESHOLD) {
      // Pause auto-scroll when user interacts
      this.pauseAutoScroll();

      this.isScrolling = true;

      if (swipeDistance > 0) {
        // Swipe up - go to next slide
        if (this.currentSection < this.totalSections - 1) {
          this.currentSection++;
        }
      } else {
        // Swipe down - go to previous slide
        if (this.currentSection > 0) {
          this.currentSection--;
        }
      }

      // Reset scrolling flag after animation
      setTimeout(() => {
        this.isScrolling = false;
      }, 1000);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isScrolling) return;

    // Pause auto-scroll when user interacts
    this.pauseAutoScroll();

    this.isScrolling = true;

    if (
      (event.key === 'ArrowDown' || event.key === ' ') &&
      this.currentSection < this.totalSections - 1
    ) {
      event.preventDefault();
      this.currentSection++;
    } else if (event.key === 'ArrowUp' && this.currentSection > 0) {
      event.preventDefault();
      this.currentSection--;
    }

    // Reset scrolling flag after animation
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
  }

  goToSection(section: number) {
    if (this.isScrolling) return;

    // Pause auto-scroll when user interacts
    this.pauseAutoScroll();

    this.isScrolling = true;
    this.currentSection = section;

    // Reset scrolling flag after animation
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
  }

  onPitchComplete() {
    // Stop auto-scroll when pitch is completed
    this.stopAutoScroll();
    this.pitchComplete.emit();
    void this.router.navigate(['/signup']);
  }

  private startAutoScroll() {
    this.autoScrollInterval = setInterval(() => {
      if (this.currentSection < this.totalSections - 1) {
        this.currentSection++;
      } else {
        // Stop auto-scroll when reaching the last slide
        this.stopAutoScroll();
      }
    }, this.AUTO_SCROLL_DELAY);
  }

  private pauseAutoScroll() {
    this.stopAutoScroll();
  }

  private stopAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }
}
